<template>
  <button
    :id="id"
    class="btn"
    :class="[
      `btn-size_${size}`,
      `btn-type_${type}`,
      `btn-color_${color}`,
      {
        'btn-fill': fill,
        'btn_icon-btn': !!icon,
        'btn-overlay': overlay,

        btn_disabled: disabled,
      },
    ]"
    :disabled="disabled"
    :type="actionType"
    v-on="$listeners"
  >
    <Spinner v-if="loading" size="20" />
    <div class="btn-container flex-centering" :class="{ 'btn-loading': loading }">
      <component :is="leftIconComponent" class="btn-icon btn-icon_left" />
      <component :is="onlyIcon" class="btn-icon btn-icon_center" />
      <slot />
      <component :is="rightIconComponent" class="btn-icon btn-icon_right" />
    </div>
  </button>
</template>

<script>
  import Spinner from 'vue-simple-spinner';

  export default {
    name: 'Button',
    components: {
      Spinner,
    },
    data() {
      return {
        rightIconComponent: this.rightIcon
          ? () => import(`@/assets/icons/component/Icon=${this.rightIcon}.svg`)
          : null,
        leftIconComponent: this.leftIcon
          ? () => import(`@/assets/icons/component/Icon=${this.leftIcon}.svg`)
          : null,
        onlyIcon: this.icon ? () => import(`@/assets/icons/component/Icon=${this.icon}.svg`) : null,
      };
    },
    props: {
      /**
       * Button type
       */
      type: {
        type: String,
        default: 'filled',
        validator: (type) => ['filled', 'ghost', 'borderless', 'func'].includes(type),
      },
      color: {
        type: String,
        default: 'primary',
        validator: (color) => ['primary', 'secondary', 'neutral', 'danger'].includes(color),
      },
      size: {
        type: String,
        default: 'medium',
        validator: (size) => ['large', 'medium', 'small'].includes(size),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      fill: {
        type: Boolean,
        default: false,
      },
      actionType: {
        type: String,
        default: 'button',
        validator: (type) => ['button', 'reset', 'submit'].includes(type),
      },
      id: [Number, String],
      rightIcon: String,
      leftIcon: String,
      icon: String,
      overlay: {
        type: Boolean,
        default: false,
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/styles/colors';

  .btn[type='submit']:not(.btn-fill) {
    margin: initial;
    width: auto !important;
  }

  .btn-size_large,
  .btn-size_medium {
    .btn-container {
      gap: 8px;
    }
  }

  .btn-size_large .btn-container .btn-icon {
    height: 24px;
    width: 24px;
  }

  .btn-size_medium .btn-container .btn-icon {
    height: 20px;
    width: 20px;
  }

  .btn-size_small,
  .btn-type_func {
    .btn-container {
      gap: 4px;
      .btn-icon {
        height: 16px;
        width: 16px;
      }
    }
  }

  .btn-fill {
    width: 100% !important;
  }

  .btn-loading {
    opacity: 0;
    height: 0;
  }

  .btn {
    &_disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    border-radius: 8px;
    font-weight: 500;
    border: 1px solid transparent;
    .btn-icon__svg {
      display: flex;
    }
    &:focus {
      outline: none;
      box-shadow: none;
    }
    &-size {
      &_large {
        padding: 15px 19px;
        font-size: 16px;
        &.btn_icon-btn {
          padding: 16px;
        }
      }
      &_medium {
        padding: 11px 17px;
        &.btn_icon-btn {
          padding: 14px;
        }
      }
      &_small {
        padding: 5px 7px;
        &.btn_icon-btn {
          padding: 6px;
        }
      }
    }
    &-type {
      &_filled {
        &.btn-color {
          &_primary {
            &:not(.btn-overlay) {
              background-color: $primary500;
              color: $white;
              & * {
                fill: white;
              }
              &:hover {
                background-color: $primary600;
              }
              &:active {
                background-color: $primary700;
              }
              &:disabled {
                background-color: $grey50;
                color: $grey300;
                & * {
                  fill: $grey300;
                }
              }
            }
            &.btn-overlay {
              background-color: white;
              color: $text-primary;
              & * {
                fill: $icon-primary;
              }
              &:hover {
                background-color: $surface1-selected;
              }
              &:disabled {
                background-color: $surface-disabled;
                color: $text-tertiary;
                & * {
                  fill: $text-tertiary;
                }
              }
            }
          }
          &_secondary {
            &.btn-overlay {
              background-color: $surface-oncolor-default;
              color: white;
              & * {
                fill: white;
              }
              &:hover {
                background-color: $surface-oncolor-selected;
              }
              &:disabled {
                background-color: $surface-disabled;
                color: $text-tertiary;
                & * {
                  fill: $text-tertiary;
                }
              }
            }
          }
          &_neutral {
            background-color: $grey100;
            color: $grey900;
            & * {
              fill: $grey900;
            }
            &:hover {
              background-color: $grey200;
            }
            &:active {
              background-color: $grey300;
            }
            &:disabled {
              background-color: $grey50;
              color: $grey300;
            }
          }
          &_danger {
            background-color: $danger600;
            color: $white;
            & * {
              fill: white;
            }
            &:hover {
              background-color: $danger700;
            }
            &:active {
              background-color: $danger800;
            }
            &:disabled {
              background-color: $grey50;
              color: $grey300;
              & * {
                fill: $grey300;
              }
            }
          }
        }
      }
      &_ghost {
        background: white;
        &.btn-color {
          &_primary {
            border-color: $primary500;
            color: $primary600;
            & * {
              fill: $primary600;
            }
            &:hover {
              border-color: $primary600;
            }
            &:active {
              border-color: $primary700;
              color: $primary700;
              & * {
                fill: $primary700;
              }
            }
            &:disabled {
              background-color: white;
              border-color: $grey100;
              color: $grey300;
              & * {
                fill: $grey300;
              }
            }
          }
          &_neutral {
            border-color: $grey200;
            color: $grey900;
            & * {
              fill: $grey900;
            }
            &:hover {
              border-color: $neutral;
            }
            &:active {
              border-color: $neutral2;
            }
            &:disabled {
              border-color: $neutral3;
              & * {
                fill: $grey300;
              }
            }
          }
        }
      }
      &_borderless {
        background: none;
        &.btn-color {
          &_primary {
            color: $primary600;
            & * {
              fill: $primary600;
            }
            &:hover {
              background-color: $primary50;
            }
            &:active {
              color: $primary700;
              & * {
                fill: $primary700;
              }
            }
            &:disabled {
              color: $grey300;
              & * {
                fill: $grey300;
              }
            }
          }
          &_neutral {
            font-weight: 600;
            color: $grey900;
            & * {
              fill: $grey900;
            }
            &:hover {
              background-color: $grey50;
            }
            &:active {
              background-color: $grey100;
            }
            &:disabled {
              color: $grey300;
              & * {
                fill: $grey300;
              }
            }
          }
          &_danger {
            color: $danger600;
            & * {
              fill: $danger600;
            }
            &:hover {
              background-color: $danger50;
            }
            &:active {
              color: $danger800;
            }
            &:disabled {
              color: $grey300;
              & * {
                fill: $grey300;
              }
            }
          }
        }
      }
      &_func {
        padding: 0;
        color: $text-primary;
        & * {
          fill: $text-primary;
        }
        &:hover {
          color: $text-secondary;
          & * {
            fill: $text-secondary;
          }
        }
        &:disabled {
          color: $text-tertiary;
          & * {
            fill: $text-tertiary;
          }
        }
      }
    }
  }
</style>
